import axios from 'axios';
import { fetchAllPages } from '../utils/riderManagement';

// Base URL configuration
// Use relative path - Vite proxy handles it in dev, Cloudflare _redirects in production
const baseURL = '/api';

const isProduction = import.meta.env.PROD;
console.log('=== API Configuration ===');
console.log('Environment:', isProduction ? 'PRODUCTION' : 'DEVELOPMENT');
console.log('API Base URL:', baseURL);
console.log('Window Location:', window.location.origin);
console.log('Full API URL will be:', window.location.origin + baseURL);
console.log('Proxy expected:', isProduction ? 'Cloudflare _redirects' : 'Vite dev server');
console.log('========================');

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Remove any 'Bearer ' prefix if it exists to prevent duplication
      const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
      config.headers.Authorization = `Bearer ${cleanToken}`;
    } else {
      console.warn('No token found in localStorage');
    }
    // Ensure content type is set for all requests
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error?.message);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 Unauthorized
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // A 401 from the login/auth request itself means "bad credentials" — let
      // the Login page show its error toast. Only a 401 on an AUTHENTICATED
      // request means the session expired, which warrants clearing the token
      // and bouncing to /login. Redirecting on the login 401 caused a hard page
      // reload that blanked the screen and swallowed the error message.
      const isAuthRequest = url.includes('/auth/');
      const alreadyOnLogin = window.location.pathname === '/login';
      if (!isAuthRequest && !alreadyOnLogin) {
        console.warn('401 Unauthorized - session expired, redirecting to login');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      // Network error or CORS issue
      console.error('Network Error:', error.message);
      return Promise.reject({
        response: {
          data: {
            message: 'Unable to connect to the server. Please check your internet connection.'
          }
        }
      });
    }
    
    // Handle specific error statuses
    if (error.response.status === 403) {
      // The full error carries the Authorization header, so log only the request.
      console.error('Access Forbidden:', error.config?.method, error.config?.url);
      // Handle token expiration or invalid token
      if (error.response.data?.message?.includes('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Never log a whole axios error: its config carries the Authorization header.
export const logError = (label, error) =>
  console.error(label, error?.response?.status ?? '', error?.message ?? '');

// The server's own error text, safe to show as-is. class-validator sends an array.
export const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(". ");
  return message || error?.response?.data?.code || fallback;
};

// Every row of a paginated admin list, 100 per request (the backend's maximum).
// Used where a filter or count must cover all rows and the server can't filter.
export const getAllPages = (path, params = {}) =>
  fetchAllPages(
    (page, limit) => api.get(path, { params: { ...params, page, limit } }).then((res) => res.data),
    100,
  ).then((result) => result.records);

// API methods
export const fetchComplaints = async (params = {}) => {
  try {
    const response = await api.get('/v1/complaints', { params });
    return response.data;
  } catch (error) {
    logError('Error fetching complaints:', error);
    throw error;
  }
};

export const createDriver = async (driverData) => {
  try {
    console.log('Sending request to /v1/admin/riders');
    const response = await api.post('/v1/admin/riders', driverData);
    return response.data;
  } catch (error) {
    logError('Error creating driver:', error);
    throw error;
  }
};

// Auth methods
export const login = async (email, password) => {
  try {
    // Admins authenticate against the dedicated admins collection.
    const loginData = { email, password };

    // Use axios instance instead of fetch for consistency
    const response = await api.post('/v1/admin/auth/login', loginData);
    
    // Store accessToken (the backend returns accessToken, not token)
    if (response.data.accessToken) {
      localStorage.setItem('token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
    }
    
    return response.data;
    
  } catch (error) {
    if (error.response) {
      throw {
        status: error.response.status,
        data: error.response.data,
        message: error.response.data?.message || 'Login failed'
      };
    }
    
    // This is a network or other error
    throw {
      status: 0,
      data: null,
      message: 'Unable to connect to the server. Please check your internet connection.'
    };
  }
};



export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete api.defaults.headers.common['Authorization'];
};

export default api;
